/**
 * Single source of truth for firing notifications via the same SW pipeline
 * used by reminders. Used by:
 *   - hooks/useReminderPolling.js (real reminder fires)
 *   - pages/Settings.js          (Test notification button)
 *
 * Returns a structured result so callers can present clear UI:
 *   { ok: true, via: 'sw' | 'page' }
 *   { ok: false, reason: <code>, detail?: string }
 *
 * Reasons:
 *   no-notification-api   — Notification global missing (very old browser / iframe)
 *   permission-default    — User hasn't decided yet (call requestNotificationPermission first)
 *   permission-denied     — User explicitly blocked (must clear in OS/browser settings)
 *   sw-error              — SW registration ready() failed or showNotification threw
 *   page-fallback-error   — Both SW and in-page Notification ctor failed
 *   unknown               — Anything else (detail attached)
 */

const ICON = "/icons/icon-192.png";
const BADGE = "/icons/icon-monochrome-96.png";

export function getPermissionStatus() {
  if (typeof Notification === "undefined") return "unsupported";
  return Notification.permission; // 'granted' | 'denied' | 'default'
}

export async function requestNotificationPermission() {
  if (typeof Notification === "undefined") {
    return { ok: false, reason: "no-notification-api" };
  }
  if (Notification.permission === "granted") {
    return { ok: true, permission: "granted" };
  }
  if (Notification.permission === "denied") {
    return { ok: false, reason: "permission-denied", permission: "denied" };
  }
  try {
    const result = await Notification.requestPermission();
    return result === "granted"
      ? { ok: true, permission: result }
      : { ok: false, reason: result === "denied" ? "permission-denied" : "permission-default", permission: result };
  } catch (e) {
    return { ok: false, reason: "unknown", detail: String(e) };
  }
}

/**
 * Fire a notification via the SAME pipeline reminders use:
 *   1. If permission != granted → fail with structured reason (no toast/no fallback)
 *   2. Prefer SW.showNotification (persists across tab close — Android requirement)
 *   3. Fall back to in-page Notification ctor only if SW path errors
 */
export async function fireNotification(title, opts = {}) {
  if (typeof Notification === "undefined") {
    return { ok: false, reason: "no-notification-api" };
  }
  const perm = Notification.permission;
  if (perm !== "granted") {
    return {
      ok: false,
      reason: perm === "denied" ? "permission-denied" : "permission-default",
    };
  }

  const payload = {
    body: opts.body || "",
    icon: opts.icon || ICON,
    badge: opts.badge || BADGE,
    tag: opts.tag,
    renotify: opts.renotify ?? false,
    requireInteraction: opts.requireInteraction ?? false,
    vibrate: opts.vibrate || [120, 60, 120],
    data: opts.data || {},
  };

  const swAvailable =
    typeof navigator !== "undefined" &&
    "serviceWorker" in navigator &&
    !!navigator.serviceWorker.controller;

  if (swAvailable) {
    try {
      const reg = await navigator.serviceWorker.ready;
      await reg.showNotification(title, payload);
      return { ok: true, via: "sw" };
    } catch (e) {
      // fall through to page-Notification fallback
      try {
        const n = new Notification(title, payload);
        n.onclick = () => {
          window.focus();
          if (payload.data?.url && window.location.pathname !== payload.data.url) {
            window.location.href = payload.data.url;
          }
          n.close();
        };
        return { ok: true, via: "page", swError: String(e) };
      } catch (e2) {
        return { ok: false, reason: "page-fallback-error", detail: String(e2) };
      }
    }
  }

  // No SW controller — use in-page Notification directly.
  try {
    const n = new Notification(title, payload);
    n.onclick = () => {
      window.focus();
      if (payload.data?.url && window.location.pathname !== payload.data.url) {
        window.location.href = payload.data.url;
      }
      n.close();
    };
    return { ok: true, via: "page" };
  } catch (e) {
    return { ok: false, reason: "page-fallback-error", detail: String(e) };
  }
}

/* ─── Last-poll timestamp (read by diagnostics panel) ───────────────────────
   useReminderPolling writes this on every tick attempt (success or fail).   */
const LAST_POLL_KEY = "journey:diag:last-poll";
const LAST_POLL_RESULT_KEY = "journey:diag:last-poll-result";

export function recordPollTick(result) {
  try {
    localStorage.setItem(LAST_POLL_KEY, new Date().toISOString());
    if (result) localStorage.setItem(LAST_POLL_RESULT_KEY, result);
  } catch {}
}

export function readLastPoll() {
  try {
    return {
      at: localStorage.getItem(LAST_POLL_KEY),
      result: localStorage.getItem(LAST_POLL_RESULT_KEY),
    };
  } catch {
    return { at: null, result: null };
  }
}
